import React from "react";
import {
  Typography,
  Button,
  Box,
  IconButton,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import type { UploadQueueItem } from "./types";
import { getFileIconColor, getFileIcon } from "./utils";

interface UploadStepProps {
  uploadQueue: UploadQueueItem[];
  isDragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onRemoveFromQueue: (id: string) => void;
  onSubmitForProcessing: () => void;
}

const UploadStep: React.FC<UploadStepProps> = ({
  uploadQueue,
  isDragOver,
  fileInputRef,
  onFileSelect,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveFromQueue,
  onSubmitForProcessing,
}) => {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 0.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "primary.main" }}
        >
          Upload Files
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload police report format templates (PDF, DOC, DOCX, TXT, RTF). Once
        uploaded, the backend will process your file and return an editable
        version.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Drag and Drop Zone */}
        <Box
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            flex: 1,
            minHeight: 200,
            border: "2px dashed",
            borderColor: isDragOver
              ? "primary.main"
              : alpha(theme.palette.primary.main, 0.3),
            borderRadius: 2,
            bgcolor: isDragOver
              ? alpha(theme.palette.primary.main, 0.04)
              : "background.default",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <input
            ref={fileInputRef}
            accept=".pdf,.doc,.docx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf,text/rtf"
            style={{ display: "none" }}
            id="template-file-input"
            type="file"
            multiple
            onChange={onFileSelect}
          />
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <UploadIcon sx={{ color: "primary.main", fontSize: 28 }} />
          </Box>
          <Typography
            variant="body1"
            sx={{ fontWeight: 500, color: "text.primary", mb: 0.5 }}
          >
            Drag and drop files here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            - OR -
          </Typography>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              py: 1,
            }}
          >
            Browse Files
          </Button>
        </Box>

        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <Box
            sx={{
              flex: 1,
              minWidth: { md: 300 },
              maxWidth: { md: 350 },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}
            >
              Uploaded Files
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                maxHeight: 5 * 64,
                overflowY: "auto",
              }}
            >
              {uploadQueue.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor:
                      item.status === "error"
                        ? alpha(theme.palette.error.main, 0.08)
                        : alpha(theme.palette.action.hover, 0.04),
                    border: "1px solid",
                    borderColor:
                      item.status === "error"
                        ? alpha(theme.palette.error.main, 0.3)
                        : alpha(theme.palette.divider, 0.5),
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: getFileIconColor(item.file.name) + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {getFileIcon(item.file.name)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.file.name}
                      {item.status !== "error" &&
                        item.status !== "completed" && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                          >
                            ({item.progress}%)
                          </Typography>
                        )}
                    </Typography>
                    {item.status === "uploading" && (
                      <LinearProgress
                        variant="determinate"
                        value={item.progress}
                        sx={{
                          mt: 0.5,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: alpha(
                            theme.palette.action.disabledBackground,
                            0.5,
                          ),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 2,
                            bgcolor: "primary.main",
                          },
                        }}
                      />
                    )}
                    {item.status === "error" && (
                      <Typography variant="caption" color="error">
                        {item.errorMessage}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {item.status === "completed" && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "success.main",
                            fontWeight: 500,
                          }}
                        >
                          Completed
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Remove file: ${item.file.name}`}
                          onClick={() => onRemoveFromQueue(item.id)}
                          sx={{
                            color: "text.secondary",
                            ml: 0.5,
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </>
                    )}
                    {(item.status === "error" ||
                      item.status === "pending" ||
                      item.status === "uploading") && (
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            item.status === "error"
                              ? "error.main"
                              : "text.secondary",
                          cursor: "pointer",
                          fontWeight:
                            item.status === "error" ? 500 : undefined,
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => onRemoveFromQueue(item.id)}
                      >
                        Cancel
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {uploadQueue.some((i) => i.status === "completed") && (
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}
                onClick={onSubmitForProcessing}
              >
                Submit for Processing
              </Button>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default UploadStep;
